// BOM correction-by-voice modal for an existing recipe.
// Seeds a draft from current material lines, applies spoken math/slot edits
// via /api/mfg/recipes/correct-bom, then persists with set-lines (preserving
// component / material-use lines).

import { useMemo, useState } from "react";
import {
  mfgPrimaryBtnLgClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { buildCorrectedRecipeLinesPayload } from "@/lib/manufacturing/buildCorrectedRecipeLinesPayload";
import { correctBomFromTranscript } from "@/lib/manufacturing/recipesDictationClient";
import { ensureFullBomReplacementDraft } from "@/lib/manufacturing/ensureFullBomReplacementDraft";
import { seedBomCorrectionDraft } from "@/lib/manufacturing/seedBomCorrectionDraft";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgMaterial, MfgRecipeDetailResponse } from "@/lib/manufacturing/types";
import { setManufacturingRecipeLines } from "@/lib/manufacturingClient";
import { ManufacturingDictatedRecipeReview } from "./ManufacturingDictatedRecipeReview";
import { ManufacturingModelsDictateMicButton } from "./ManufacturingModelsDictateMicButton";

function normalizeDraft(draft: DictatedRecipeDraft): DictatedRecipeDraft {
  return {
    ...draft,
    lines: draft.lines.map((ln) => ({
      ...ln,
      slot_label: normalizeSlotLabel(ln.slot_label),
    })),
  };
}

type Phase =
  | { kind: "capture" }
  | { kind: "applying" }
  | { kind: "review"; draft: DictatedRecipeDraft; transcript: string }
  | { kind: "applyError"; message: string; draft: DictatedRecipeDraft };

export function ManufacturingModelsBomCorrectModal({
  detail,
  materials,
  orgSlotLabels = [],
  onClose,
  onSaved,
}: {
  detail: MfgRecipeDetailResponse;
  materials: ReadonlyArray<MfgMaterial>;
  orgSlotLabels?: readonly string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const seeded = useMemo(() => seedBomCorrectionDraft(detail), [detail]);
  const [phase, setPhase] = useState<Phase>({ kind: "capture" });
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function applyCorrection(transcript: string) {
    const trimmed = transcript.trim();
    if (!trimmed) return;
    setPhase({ kind: "applying" });
    const result = await correctBomFromTranscript({
      priorDraft: seeded,
      transcript: trimmed,
    });
    if (result.ok && result.data.ok && result.data.draft) {
      setPhase({
        kind: "review",
        draft: normalizeDraft(ensureFullBomReplacementDraft(seeded, result.data.draft)),
        transcript: trimmed,
      });
      return;
    }
    const message = result.ok
      ? "Could not apply correction. Try rephrasing or hand-edit."
      : result.error || "BOM correction failed";
    setPhase({
      kind: "applyError",
      message,
      draft: normalizeDraft(result.ok ? result.data.draft ?? seeded : seeded),
    });
  }

  async function handleSave(draft: DictatedRecipeDraft) {
    setSaveBusy(true);
    setSaveError(null);
    const built = buildCorrectedRecipeLinesPayload(draft, detail);
    if (!built.ok) {
      setSaveBusy(false);
      setSaveError(built.error);
      return;
    }
    const result = await setManufacturingRecipeLines({
      recipe_id: detail.recipe.id,
      lines: built.lines,
    });
    setSaveBusy(false);
    if (!result.ok) {
      setSaveError(result.error || "Failed to save BOM lines");
      return;
    }
    onSaved();
    onClose();
  }

  const reviewDraft =
    phase.kind === "review"
      ? phase.draft
      : phase.kind === "applyError"
        ? phase.draft
        : null;
  const reviewTranscript = phase.kind === "review" ? phase.transcript : "";

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          Correct BOM by voice — {detail.recipe.code}
        </h3>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Example: “Perimeter of 8 by 16, divide by 1.25, add 4, round up — that’s wall_studs.
          Wall studs times 7 is framing_nails. Perimeter times 12.5 minus 40 is siding_nails.
          Also add 2x4x196 pre-cut stud, perimeter times 4, slot extra_frame_bracing.”
        </p>

        {phase.kind === "capture" || phase.kind === "applying" ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/40">
              <div className="font-medium text-gray-800 dark:text-zinc-100">
                Current material slots ({seeded.lines.length})
              </div>
              <ul className="mt-1 max-h-28 overflow-y-auto text-[11px] text-gray-600 dark:text-zinc-300">
                {seeded.lines.map((l, i) => (
                  <li key={`${l.slot_label}-${i}`}>
                    <code>{l.slot_label || "(no slot)"}</code> · {l.material_name_heard} · qty{" "}
                    {l.quantity}
                  </li>
                ))}
              </ul>
            </div>
            {phase.kind === "applying" ? (
              <div className="text-sm text-gray-600 dark:text-zinc-300">Applying correction…</div>
            ) : (
              <ManufacturingModelsDictateMicButton
                onComplete={(t) => void applyCorrection(t)}
                extractLabel="Apply correction →"
              />
            )}
            <div className="flex justify-end">
              <button type="button" className={mfgSecondaryBtnClass} onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        {phase.kind === "applyError" ? (
          <div className="mt-3 space-y-3">
            <div className="text-xs text-red-600 dark:text-red-400">{phase.message}</div>
            <button
              type="button"
              className={mfgPrimaryBtnLgClass}
              onClick={() => setPhase({ kind: "capture" })}
            >
              Try again
            </button>
          </div>
        ) : null}

        {reviewDraft && phase.kind === "review" ? (
          <div className="mt-4">
            <ManufacturingDictatedRecipeReview
              transcript={reviewTranscript}
              draft={reviewDraft}
              priorDraft={seeded}
              onDraftChange={(next) =>
                setPhase({
                  kind: "review",
                  draft: normalizeDraft(ensureFullBomReplacementDraft(seeded, next)),
                  transcript: reviewTranscript,
                })
              }
              onRedictate={() => setPhase({ kind: "capture" })}
              onClose={onClose}
              onSave={() => void handleSave(reviewDraft)}
              saveBusy={saveBusy}
              saveError={saveError}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              mode="bom_correct"
              saveLabel="Replace material lines"
              redictateLabel="New correction"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
